import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { createForm, updateForm, getFormResponses } from '../../api';

const inp = { padding: '8px 11px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };

const TYPE_OPTIONS = [
  { value: 'text',            label: 'Text' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'checkbox',        label: 'Checkbox (multi-select)' },
  { value: 'scale',           label: 'Scale' },
  { value: 'yesno',           label: 'Yes / No' },
];

let keyCounter = 0;
const nextKey = () => `q${Date.now()}_${keyCounter++}`;

function emptyQuestion() {
  return { _key: nextKey(), type: 'text', question_text: '', options: [], scale_min: 1, scale_max: 10, scale_min_label: '', scale_max_label: '', required: true };
}

export default function FormBuilder({ formId, user, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([emptyQuestion()]);
  const [loading, setLoading] = useState(!!formId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!formId) return;
    getFormResponses(formId).then(({ form, questions: qs }) => {
      setTitle(form.title);
      setDescription(form.description || '');
      setQuestions((qs || []).map(q => ({
        _key: nextKey(), id: q.id, type: q.type, question_text: q.question_text,
        options: q.options || [], scale_min: q.scale_min ?? 1, scale_max: q.scale_max ?? 10,
        scale_min_label: q.scale_min_label || '', scale_max_label: q.scale_max_label || '', required: q.required,
      })));
    }).finally(() => setLoading(false));
  }, [formId]);

  const updateQ = (key, patch) => setQuestions(qs => qs.map(q => q._key === key ? { ...q, ...patch } : q));
  const addQ = () => setQuestions(qs => [...qs, emptyQuestion()]);
  const removeQ = (key) => setQuestions(qs => qs.filter(q => q._key !== key));
  const moveQ = (key, dir) => setQuestions(qs => {
    const i = qs.findIndex(q => q._key === key);
    const j = i + dir;
    if (j < 0 || j >= qs.length) return qs;
    const copy = [...qs];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const addOption = (key) => updateQ(key, { options: [...(questions.find(q => q._key === key)?.options || []), ''] });
  const setOption = (key, i, value) => setQuestions(qs => qs.map(q => q._key === key ? { ...q, options: q.options.map((o, oi) => oi === i ? value : o) } : q));
  const removeOption = (key, i) => setQuestions(qs => qs.map(q => q._key === key ? { ...q, options: q.options.filter((_, oi) => oi !== i) } : q));

  const handleSave = async () => {
    setError('');
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!questions.length) { setError('Add at least one question.'); return; }
    for (const q of questions) {
      if (!q.question_text.trim()) { setError('Every question needs text.'); return; }
      if ((q.type === 'multiple_choice' || q.type === 'checkbox') && q.options.filter(o => o.trim()).length < 2) {
        setError(`"${q.question_text}" needs at least 2 options.`); return;
      }
    }
    setSaving(true);
    const payload = {
      title: title.trim(), description: description.trim() || null,
      created_by_user_id: user?.id,
      questions: questions.map(q => ({
        type: q.type, question_text: q.question_text.trim(),
        options: (q.type === 'multiple_choice' || q.type === 'checkbox') ? q.options.filter(o => o.trim()) : null,
        scale_min: q.type === 'scale' ? Number(q.scale_min) : null,
        scale_max: q.type === 'scale' ? Number(q.scale_max) : null,
        scale_min_label: q.type === 'scale' ? (q.scale_min_label || null) : null,
        scale_max_label: q.type === 'scale' ? (q.scale_max_label || null) : null,
        required: !!q.required,
      })),
    };
    try {
      if (formId) await updateForm(formId, payload);
      else await createForm(payload);
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save form.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, margin: 0 }}>{formId ? 'Edit form' : 'New form'}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 6 }}><X size={18} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1, padding: '16px 24px 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              <input value={title} onChange={e => setTitle(e.target.value)} style={inp} placeholder="Form title" />
              <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...inp, minHeight: 60, resize: 'vertical' }} placeholder="Description (optional)" />
            </div>

            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Questions</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {questions.map((q, i) => (
                <div key={q._key} style={{ border: '1px solid var(--border-strong)', borderRadius: 10, padding: 14, background: 'var(--bg-app)' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <button onClick={() => moveQ(q._key, -1)} disabled={i === 0}
                        style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 5, color: i === 0 ? 'var(--text-dimmed)' : 'var(--text-secondary)', cursor: i === 0 ? 'default' : 'pointer', padding: 2 }}><ArrowUp size={12} /></button>
                      <button onClick={() => moveQ(q._key, 1)} disabled={i === questions.length - 1}
                        style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 5, color: i === questions.length - 1 ? 'var(--text-dimmed)' : 'var(--text-secondary)', cursor: i === questions.length - 1 ? 'default' : 'pointer', padding: 2 }}><ArrowDown size={12} /></button>
                    </div>
                    <input value={q.question_text} onChange={e => updateQ(q._key, { question_text: e.target.value })} style={{ ...inp, flex: 1 }} placeholder={`Question ${i + 1}`} />
                    <select value={q.type} onChange={e => updateQ(q._key, { type: e.target.value })} style={{ ...inp, width: 170 }}>
                      {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <button onClick={() => removeQ(q._key)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><Trash2 size={15} /></button>
                  </div>

                  {(q.type === 'multiple_choice' || q.type === 'checkbox') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 32 }}>
                      {q.options.map((opt, oi) => (
                        <div key={oi} style={{ display: 'flex', gap: 6 }}>
                          <input value={opt} onChange={e => setOption(q._key, oi, e.target.value)} style={inp} placeholder={`Option ${oi + 1}`} />
                          <button onClick={() => removeOption(q._key, oi)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={14} /></button>
                        </div>
                      ))}
                      <button onClick={() => addOption(q._key)}
                        style={{ alignSelf: 'flex-start', background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Plus size={12} /> Add option
                      </button>
                    </div>
                  )}

                  {q.type === 'scale' && (
                    <div style={{ display: 'flex', gap: 8, marginLeft: 32, flexWrap: 'wrap' }}>
                      <input type="number" value={q.scale_min} onChange={e => updateQ(q._key, { scale_min: e.target.value })} style={{ ...inp, width: 70 }} placeholder="Min" />
                      <input type="number" value={q.scale_max} onChange={e => updateQ(q._key, { scale_max: e.target.value })} style={{ ...inp, width: 70 }} placeholder="Max" />
                      <input value={q.scale_min_label} onChange={e => updateQ(q._key, { scale_min_label: e.target.value })} style={{ ...inp, width: 140 }} placeholder="Min label (optional)" />
                      <input value={q.scale_max_label} onChange={e => updateQ(q._key, { scale_max_label: e.target.value })} style={{ ...inp, width: 140 }} placeholder="Max label (optional)" />
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, marginLeft: 32, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={q.required} onChange={e => updateQ(q._key, { required: e.target.checked })} />
                    Required
                  </label>
                </div>
              ))}
            </div>

            <button onClick={addQ}
              style={{ marginTop: 12, background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 8, color: 'var(--text-secondary)', cursor: 'pointer', padding: '8px 14px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} /> Add question
            </button>

            {error && <p style={{ color: '#ef4444', fontSize: 13, marginTop: 14 }}>{error}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button disabled={saving} onClick={handleSave}
                style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
